import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";

/**
 * /notes — placeholder page (architecture only).
 * Future home for short essays on engineering, design and the craft.
 * No CMS yet. Topics list is the editorial roadmap.
 */

const EASE = [0.16, 1, 0.3, 1];

const TOPICS = [
  "Why most agency websites look identical",
  "Building AI systems people actually use",
  "Thoughts on automation",
  "Behind PARABOLUM",
  "Designing for longevity",
];

export default function NotesIndex() {
  useEffect(() => {
    document.title = "Notes — Parabolum";
    window.scrollTo(0, 0);
  }, []);

  return (
    <main
      data-testid="notes-page"
      className="relative min-h-screen overflow-hidden bg-[#050507] text-white"
    >
      <div className="grid-faint pointer-events-none absolute inset-0 opacity-[0.04]" />

      <div className="relative mx-auto flex min-h-screen max-w-[1400px] flex-col px-6 pb-24 pt-28 md:px-12 md:pb-32 md:pt-36">
        <div className="mb-16 md:mb-24">
          <Link
            to="/"
            data-testid="notes-back-link"
            data-cursor="hover"
            className="group inline-flex items-center gap-2 font-body text-[12px] uppercase tracking-[0.28em] text-white/45 transition-colors hover:text-white"
          >
            <span className="inline-block transition-transform duration-500 group-hover:-translate-x-1">
              ←
            </span>
            Parabolum
          </Link>
        </div>

        <header className="mb-20 max-w-3xl md:mb-28">
          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-7 flex items-center gap-3"
          >
            <span className="h-px w-8 bg-white/30" />
            <span className="font-body text-[11px] uppercase tracking-[0.35em] text-white/50">
              Notes
            </span>
          </motion.div>
          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.05 }}
            className="font-display text-5xl font-bold leading-[1] tracking-tight text-white sm:text-6xl md:text-7xl lg:text-[5.4rem]"
          >
            Short essays on{" "}
            <span className="font-serif-italic font-normal text-white/55">
              craft and engineering.
            </span>
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.15 }}
            className="font-body mt-7 max-w-xl text-base leading-relaxed text-white/55 md:text-lg"
          >
            We&apos;re writing them now. The first ones will appear here shortly.
          </motion.p>
        </header>

        <section className="mb-12 grid items-start gap-10 md:grid-cols-12">
          <div className="md:col-span-4">
            <span className="font-body text-[11px] uppercase tracking-[0.32em] text-white/50">
              Coming soon
            </span>
          </div>
          <div className="md:col-span-8">
            <ul className="flex flex-col divide-y divide-white/[0.06] border-y border-white/[0.06]">
              {TOPICS.map((topic, i) => (
                <motion.li
                  key={topic}
                  initial={{ opacity: 0, y: 14 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-10%" }}
                  transition={{ duration: 0.6, ease: EASE, delay: 0.05 * i }}
                  className="flex items-baseline justify-between py-5"
                >
                  <span className="font-display text-xl tracking-tight text-white/85 md:text-2xl">
                    {topic}
                  </span>
                  <span className="font-body text-[11px] uppercase tracking-[0.3em] text-white/35">
                    Draft
                  </span>
                </motion.li>
              ))}
            </ul>
          </div>
        </section>

        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.45 }}
          className="font-serif-italic mt-20 max-w-2xl text-[15px] text-white/40 md:mt-28"
        >
          We write the way we build — slowly, on purpose.
        </motion.p>
      </div>
    </main>
  );
}
